import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../../utils/authUtils';
import { useLanguage } from '../../utils/LanguageContext';
import { getLatestPromo } from '../../utils/api';

const PROMO_SEEN_KEY = 'rumahkira_promo_seen';

const PromoToast = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [promo, setPromo] = useState(null);
  const [copied, setCopied] = useState(false);

  const t = {
    en: {
      welcome: 'Welcome! Use this promo code for a free pass:',
      copied: 'Copied!',
    },
    my: {
      welcome: 'Selamat datang! Guna kod promo ini untuk pas percuma:',
      copied: 'Disalin!',
    },
  };

  const txt = t[language] || t.en;

  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem(PROMO_SEEN_KEY)) return;

    let cancelled = false;

    const fetchPromo = async () => {
      try {
        const res = await getLatestPromo();
        if (cancelled) return;
        if (res.success && res.data) {
          setPromo(res.data);
          setTimeout(() => {
            if (!cancelled) setOpen(true);
          }, 1500);
        }
      } catch {
        // Silent fail — no promo available
      }
    };

    fetchPromo();
    return () => { cancelled = true; };
  }, [user]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
    localStorage.setItem(PROMO_SEEN_KEY, '1');
  };

  const handleCopy = async () => {
    if (!promo) return;
    try {
      await navigator.clipboard.writeText(promo.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback — select text for manual copy
    }
  };

  if (!user || !promo) return null;

  return (
    <Snackbar
      open={open}
      autoHideDuration={10000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={handleClose}
        severity="success"
        icon={<CardGiftcardIcon />}
        sx={{ width: '100%', maxWidth: 440, alignItems: 'center' }}
      >
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {txt.welcome}
        </Typography>
        <Box
          onClick={handleCopy}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'rgba(0,0,0,0.08)',
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.12)' },
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}
          >
            {promo.code}
          </Typography>
          <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
          {copied && (
            <Typography variant="caption" color="primary">
              {txt.copied}
            </Typography>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default PromoToast;
